import React, { useRef } from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, InjectedFormProps } from 'redux-form'
import styled from 'styled-components'

import useDispatch from 'hooks/useDispatch'
import { signInUser } from 'actions/authActions'
import { renderTextField, Submit } from 'components/form'
import { validRequired, validEmail } from 'components/form/validate'
import { StyledLink } from 'components/display/Typography'
import { AuthToken, FacebookLogin, GoogleLogin, AppleLogin } from './shared'
import { Divider, FieldWrapper } from './styles'

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  font-weight: 300;
  gap: 8px;
`

interface FormValues {
  user: {
    email: string
    password: string
    remember_me: boolean
  }
}

interface LoginProps {
  auth: { loading: boolean }
  onSuccess?: () => any
  onConfirmEmail: () => any
  onResetPassword: () => any
  serverSide?: boolean
}

const Login = ({
  handleSubmit,
  auth,
  onSuccess,
  onConfirmEmail,
  onResetPassword,
  serverSide = false,
}: LoginProps & InjectedFormProps) => {
  const dispatch = useDispatch()
  const formElement = useRef<HTMLFormElement>()

  return (
    <>
      <AppleLogin>Sign in with Apple</AppleLogin>
      <GoogleLogin>Sign in with Google</GoogleLogin>
      <FacebookLogin
        disabled={auth.loading}
        serverSide={serverSide}
        onSuccess={onSuccess}
        onConfirmEmail={onConfirmEmail}
      >
        Sign in with Facebook
      </FacebookLogin>

      <form
        className="qa-login"
        action="/users/sign_in"
        method="post"
        ref={formElement}
        onSubmit={handleSubmit((formValues) => {
          if (serverSide) {
            formElement.current.submit()
          } else {
            // @ts-ignore -- signInUser not typed
            dispatch(signInUser(formValues)).then(onSuccess)
          }
        })}
      >
        <AuthToken />

        <Divider>
          <span>or</span>
        </Divider>

        <FieldWrapper>
          <Field
            label="Email"
            name="user[email]"
            type="email"
            component={renderTextField}
            validate={[validRequired, validEmail]}
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper>
          <Field
            label="Password"
            name="user[password]"
            type="password"
            component={renderTextField}
            validate={[validRequired]}
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper margin="medium">
          <CheckboxWrapper>
            <Field component="input" type="checkbox" name="user[remember_me]" />{' '}
            Remember me
          </CheckboxWrapper>
        </FieldWrapper>

        <FieldWrapper margin="medium">
          <Submit
            disabled={auth.loading}
            fullwidth
            uppercase={false}
            aria-label="Submit login form"
          >
            Login
          </Submit>
        </FieldWrapper>

        <StyledLink
          as="button"
          type="button"
          onClick={onResetPassword}
          style={{ textAlign: 'center', display: 'block', margin: '0 auto' }}
        >
          Forgot password?
        </StyledLink>
      </form>
    </>
  )
}

const LoginForm = reduxForm<FormValues, LoginProps>({
  form: 'Login',
})(Login)

const ConnectedLoginForm = connect<{ auth: { loading: boolean } }>(
  ({ auth }: { auth: { loading: boolean } }) => ({
    auth,
    initialValues: {
      user: {
        remember_me: true,
      },
    },
  })
)(LoginForm)

export default ConnectedLoginForm
